import React, {useCallback, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    ARCHIVED_COLOR,
    DISABLED,
    EURO_VALUE,
    GLOBAL_BACKGROUND_POPUP_ABSOLUTE,
    GLOBAL_BORDER_RADIUS,
    operationOnRentItemType,
    SELLABLE_COLOR,
} from "../../../costants";
import {httpsCallable} from "firebase/functions";
import {functions} from "../../../firebase";
import {getUrlImg} from "../../../global/firebaseStorageRepo";
import Loader from "../../commons/loader";
import {Calendar, momentLocalizer} from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {fromMomentDate, toMomentDate} from "../../../global/dates";
import MainButton from "../../commons/mainButton";
import MyNormalText from "../../commons/myNormalText";
import {globalElementInputStyle} from "../../../global/style/globalElementInputStyle";
import useMyEscapeListener from "../../../global/listeners/useMyEscapeListener";
import useMyClickOutListener from "../../../global/listeners/useMyClickOutListener";
import UpdateItem from "./updateItem";

moment.locale('it');
const localizer = momentLocalizer(moment);

// Componente per le immagini
const ItemImages = ({images, rentId, itemName}) => {
    const [urlImages, setUrlImages] = useState([]);

    useEffect(() => {
        const loadImages = async () => {
            try {
                const urls = await Promise.all(
                    images?.map(async (image) =>
                        getUrlImg(rentId, itemName, "detail", image)
                    )
                );
                setUrlImages(urls);
            } catch (error) {
                toast.error("Errore nel caricamento delle immagini");
            }
        };
        loadImages();
    }, [images, rentId, itemName]);

    return (
        <ImagesContainer>
            {urlImages.map((url, index) => (
                <ImageWrapper key={index}>
                    <img
                        src={url}
                        alt={`Immagine articolo ${index + 1}`}
                        style={{width: 100, height: 100, objectFit: "cover", borderRadius: 4}}
                    />
                </ImageWrapper>
            ))}
        </ImagesContainer>
    );
};

const SellableSection = ({
                             isSellable,
                             setIsSellable,
                             oldSellPrice,
                             setOldSellPrice,
                             newSellPrice,
                             setNewSellPrice,
                             italianDescription,
                             setItalianDescription,
                             englishDescription,
                             setEnglishDescription,
                             germanDescription,
                             setGermanDescription,
                             handleUpdateSellableInfos,
                             loadingUpdatingSellableInfos,
                             premiumPalette,
                         }) => {
    const handleChangeDescription = (setDescription) => (event) =>
        setDescription(event.target.value);

    return (
        <Section backgroundColor={premiumPalette.primaryColorSecondAlternative}>
            <MyNormalText text="Vendita" bold fontSize={18}/>
            <SellableStatus>
                <MyNormalText
                    text={isSellable ? "Ora in vendita" : "Ora non in vendita"}
                    bold
                    color={isSellable ? "green" : "red"}
                />
                <CheckboxContainer>
                    <MyNormalText text="In vendita"/>
                    <input
                        type="checkbox"
                        checked={isSellable}
                        onChange={() => setIsSellable(!isSellable)}
                        style={{width: 20, height: 20}}
                        aria-label="Attiva/Disattiva vendita"
                    />
                </CheckboxContainer>
            </SellableStatus>
            <InputGroup>
                <input
                    style={globalElementInputStyle.customInput}
                    type="number"
                    min="0"
                    value={oldSellPrice || ""}
                    placeholder="Prezzo di listino"
                    onChange={(e) => setOldSellPrice(parseInt(e.target.value) || 0)}
                    aria-label="Prezzo di listino"
                />
                <input
                    style={globalElementInputStyle.customInput}
                    type="number"
                    min="0"
                    value={newSellPrice || ""}
                    placeholder="Prezzo di vendita"
                    onChange={(e) => setNewSellPrice(parseInt(e.target.value) || 0)}
                    aria-label="Prezzo di vendita"
                />
            </InputGroup>
            <TextArea
                value={italianDescription || ""}
                placeholder="Descrizione in italiano"
                onChange={handleChangeDescription(setItalianDescription)}
                rows={italianDescription?.split("\n").length || 1}
                aria-label="Descrizione in italiano"
            />
            <TextArea
                value={englishDescription || ""}
                placeholder="Descrizione in inglese"
                onChange={handleChangeDescription(setEnglishDescription)}
                rows={englishDescription?.split("\n").length || 1}
                aria-label="Descrizione in inglese"
            />
            <TextArea
                value={germanDescription || ""}
                placeholder="Descrizione in tedesco"
                onChange={handleChangeDescription(setGermanDescription)}
                rows={germanDescription?.split("\n").length || 1}
                aria-label="Descrizione in tedesco"
            />
            <MainButton
                text="Aggiorna"
                action={handleUpdateSellableInfos}
                loading={loadingUpdatingSellableInfos}
                disabled={loadingUpdatingSellableInfos}
                backgroundColor={premiumPalette.secondaryColor}
                aria-label="Aggiorna informazioni di vendita"
            />
        </Section>
    );
};

function ItemDetail({rentUser, itemId, premiumPalette, setIsOpen, openOperations, refreshItems}) {
    const primaryColorSecondAlternative = premiumPalette.primaryColorSecondAlternative;
    const [item, setItem] = useState({});
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [loadingItem, setLoadingItem] = useState(true);
    const [loadingUpdatingItem, setLoadingUpdatingItem] = useState(false);
    const [loadingUpdatingSellableInfos, setLoadingUpdatingSellableInfos] = useState(false);
    const [loadingDeletingItemDate, setLoadingDeletingItemDate] = useState(false);
    const [loadingArchivingItem, setLoadingArchivingItem] = useState(false);
    const [loadingDuplicateItem, setLoadingDuplicateItem] = useState(false);
    const [selectedRange, setSelectedRange] = useState(null);
    const [dateBlockedToRemove, setDateBlockedToRemove] = useState(null);
    const [isSellable, setIsSellable] = useState(false);
    const [oldSellPrice, setOldSellPrice] = useState();
    const [newSellPrice, setNewSellPrice] = useState();
    const [italianDescription, setItalianDescription] = useState("");
    const [englishDescription, setEnglishDescription] = useState("");
    const [germanDescription, setGermanDescription] = useState("");

    const itemDetailRef = useRef();
    const rentSlotConfiguration = rentUser.rentSlotConfiguration;

    useMyEscapeListener(() => setIsOpen(false));
    useMyClickOutListener(itemDetailRef, () => setIsOpen(false));

    const getItem = useCallback(async () => {
        setLoadingItem(true);
        try {
            const response = await httpsCallable(functions, "getItemRentDetail")({
                rentId: rentUser.rentId,
                itemId,
            });
            const item = response.data.item;
            setItem(item);
            setAllTheRentedDates(item.rentedDays);
            setIsSellable(item.isSellable);
            const sellableInfos = item.sellableInfos;
            setOldSellPrice(sellableInfos?.oldPrice);
            setNewSellPrice(sellableInfos?.newPrice);
            setItalianDescription(sellableInfos?.description?.italian || "");
            setEnglishDescription(sellableInfos?.description?.english || "");
            setGermanDescription(sellableInfos?.description?.german || "");
        } catch (error) {
            toast.error("Errore nel caricamento dell'articolo");
        } finally {
            setLoadingItem(false);
        }
    }, [rentUser.rentId, itemId]);

    const setAllTheRentedDates = (rentedDates) => {
        const parsedDates = rentedDates?.map((date) => {
            const momentStartDate = toMomentDate(date.from);
            const momentEndDate = date.to ? toMomentDate(date.to).add(1, "day") : momentStartDate;
            return {
                id: `${momentStartDate.toISOString()}/${momentEndDate.toISOString()}`,
                start: momentStartDate,
                end: momentEndDate,
                title: date.type === operationOnRentItemType.BLOCKED ? "Bloccato" : "Prenotato",
                allDay: true,
            };
        }) || [];
        setCalendarEvents(parsedDates);
    };

    const handleSelectRange = ({start, end}) => {
        const momentStartDate = toMomentDate(start);
        const momentEndDate = toMomentDate(end);
        const parsedStartDateForBe = fromMomentDate(momentStartDate);
        const parsedEndDateForBe = fromMomentDate(momentEndDate.clone().subtract(1, "day"));
        setSelectedRange({from: parsedStartDateForBe, to: parsedEndDateForBe});
        const calendarEventsParsed = calendarEvents.filter((event) => event.title !== "Blocca");
        setCalendarEvents([
            ...calendarEventsParsed,
            {
                id: `${momentStartDate.toISOString()}/${momentEndDate.toISOString()}`,
                start: momentStartDate,
                end: momentEndDate,
                title: "Blocca",
                allDay: true,
            },
        ]);
    };

    const eventPropGetter = (event, start, end, isSelected) => {
        let style = {};
        if (isSelected && event.title === "Bloccato") {
            style.backgroundColor = "red";
            let momentStartDate = event.start;
            let momentEndDate = event.end.clone();
            if (momentStartDate.isSame(momentEndDate)) {
                momentStartDate = momentEndDate;
            } else {
                momentEndDate.subtract(1, "day");
            }
            if (!dateBlockedToRemove) {
                setDateBlockedToRemove({
                    from: fromMomentDate(momentStartDate),
                    to: fromMomentDate(momentEndDate),
                });
            }
        }
        return {style};
    };

    const handleSaveNewRentedDays = async () => {
        setLoadingUpdatingItem(true);
        try {
            const response = await httpsCallable(functions, "rentUpdateItemInfoCommand")({
                itemId,
                rentId: rentUser.rentId,
                rentedDays: {from: selectedRange.from, to: selectedRange.to},
                operationType: operationOnRentItemType.BLOCKED,
            });
            if (response.data.response === "OK") {
                toast.success("Date bloccate con successo");
                getItem();
            } else {
                toast.error("Errore nel bloccare le date");
            }
        } catch {
            toast.error("Errore nel bloccare le date");
        } finally {
            setLoadingUpdatingItem(false);
        }
    };

    const handleUpdateSellableInfos = async () => {
        setLoadingUpdatingSellableInfos(true);
        try {
            const response = await httpsCallable(functions, "rentUpdateItemInfoCommand")({
                itemId,
                rentId: rentUser.rentId,
                isSellable,
                sellableInfos: {
                    oldPrice: oldSellPrice,
                    newPrice: newSellPrice,
                    description: {
                        italian: italianDescription,
                        english: englishDescription,
                        german: germanDescription,
                    },
                },
                operationType: operationOnRentItemType.UPDATE_SELLABLE_INFOS,
            });
            if (response.data.response === "OK") {
                toast.success("Informazioni di vendita aggiornate");
                refreshItems();
                getItem();
            } else {
                toast.error("Errore nell'aggiornamento delle informazioni");
            }
        } catch {
            toast.error("Errore nell'aggiornamento delle informazioni");
        } finally {
            setLoadingUpdatingSellableInfos(false);
        }
    };

    const handleRemoveRentedDays = async () => {
        if (dateBlockedToRemove) {
            setLoadingDeletingItemDate(true);
            try {
                const response = await httpsCallable(functions, "deleteRentItemInfo")({
                    rentId: rentUser.rentId,
                    itemId,
                    rentedDays: dateBlockedToRemove,
                    operationType: operationOnRentItemType.BLOCKED,
                });
                if (response.data.response === "OK") {
                    toast.success("Date sbloccate con successo");
                    getItem();
                } else {
                    toast.error("Errore nella rimozione delle date");
                }
            } catch {
                toast.error("Errore nella rimozione delle date");
            } finally {
                setLoadingDeletingItemDate(false);
            }
        }
    };

    const handleArchiveItem = async () => {
        setLoadingArchivingItem(true);
        try {
            const response = await httpsCallable(functions, "rentUpdateItemInfoCommand")({
                rentId: rentUser.rentId,
                itemId,
                archived: !item.archived,
                operationType: operationOnRentItemType.ARCHIVE,
            });
            if (response.data.response === "OK") {
                toast.success(item.archived ? "Articolo ripristinato" : "Articolo archiviato");
                getItem();
                refreshItems();
            } else {
                toast.error("Errore nell'archiviazione");
            }
        } catch {
            toast.error("Errore nell'archiviazione");
        } finally {
            setLoadingArchivingItem(false);
        }
    };

    const handleDuplicateItem = async () => {
        setLoadingDuplicateItem(true);
        try {
            const response = await httpsCallable(functions, "rentDuplicateItemCommand")({
                rentId: rentUser.rentId,
                item,
            });
            toast.success(`Nuovo articolo creato con codice ${response.data.item.id}`);
            refreshItems();
        } catch {
            toast.error("Errore nella duplicazione dell'articolo");
        } finally {
            setLoadingDuplicateItem(false);
        }
    };

    useEffect(() => {
        getItem();
    }, [getItem]);

    return (
        <ContainerFullPage>
            <ToastContainer position="top-right" autoClose={3000}/>
            {loadingItem || !item.id ? (
                <Loader absoluteFullPage/>
            ) : (
                <Container ref={itemDetailRef}>
                    <CloseButton onClick={() => setIsOpen(false)} aria-label="Chiudi dettagli articolo">
                        ✕
                    </CloseButton>
                    <Section backgroundColor={primaryColorSecondAlternative}>
                        <ItemImages images={item.images} rentId={rentUser.rentId} itemName={item.name}/>
                        <ItemInfo>
                            <MyNormalText text={item.name} bold fontSize={18}/>
                            <MyNormalText text={item.id}/>
                            <SubSection>
                                <MyNormalText text="Codice magazzino" bold/>
                                <MyNormalText text={item?.assignedRentCode || "-"}/>
                            </SubSection>
                            {item.archived && (
                                <SubSection>
                                    <StatusContainer borderColor={ARCHIVED_COLOR}>
                                        <MyNormalText text="Archiviato" bold/>
                                    </StatusContainer>
                                </SubSection>
                            )}
                            {item.isSellable && (
                                <SubSection>
                                    <StatusContainer borderColor={SELLABLE_COLOR}>
                                        <MyNormalText text="In vendita" bold/>
                                    </StatusContainer>
                                </SubSection>
                            )}
                            <SubSection>
                                <InfoContainer>
                                    <MyNormalText text={`${item?.category}, ${item?.size}`} bold/>
                                </InfoContainer>
                            </SubSection>
                            <SubSection>
                                <PriceContainer>
                                    <InfoContainer>
                                        <MyNormalText text="Prezzi giornalieri" bold/>
                                        {rentSlotConfiguration.map((slot, index) => {
                                            const slotFound = item.singleDayPrices.find((price) => price.type === slot.type);
                                            return (
                                                <MyNormalText
                                                    key={index}
                                                    text={`${slot.description}: ${EURO_VALUE}${slotFound.price}`}
                                                    fontSize={14}
                                                />
                                            );
                                        })}
                                    </InfoContainer>
                                    <InfoContainer>
                                        <MyNormalText text="Prezzi su più giorni" bold/>
                                        {item?.prices.map((price, index) => (
                                            <MyNormalText
                                                key={index}
                                                text={`Giorno ${price.day}: ${EURO_VALUE}${price.cost}`}
                                            />
                                        ))}
                                    </InfoContainer>
                                </PriceContainer>
                            </SubSection>
                            <InfoContainer>
                                {item?.infos?.map((info, index) => (
                                    <DescriptionContainer key={index}>
                                        <MyNormalText text="Descrizione" bold/>
                                        <MyNormalText text={info.description} multiline/>
                                    </DescriptionContainer>
                                ))}
                            </InfoContainer>
                        </ItemInfo>
                    </Section>
                    {openOperations && (
                        <>
                            <Section backgroundColor={primaryColorSecondAlternative}>
                                <MyNormalText text="Archiviazione" bold fontSize={18}/>
                                <MainButton
                                    backgroundColor={premiumPalette.secondaryColor}
                                    loading={loadingArchivingItem}
                                    disabled={loadingArchivingItem}
                                    text={item.archived ? "Rimuovi dall'archivio" : "Archivia"}
                                    action={handleArchiveItem}
                                    aria-label={item.archived ? "Rimuovi dall'archivio" : "Archivia articolo"}
                                />
                            </Section>
                            <Section backgroundColor={primaryColorSecondAlternative}>
                                <MyNormalText text="Aggiorna informazioni" bold fontSize={18}/>
                                <UpdateItem
                                    rentUser={rentUser}
                                    item={item}
                                    premiumPalette={premiumPalette}
                                    refreshItems={refreshItems}
                                    refreshItem={getItem}
                                />
                            </Section>
                            <SellableSection
                                isSellable={isSellable}
                                setIsSellable={setIsSellable}
                                oldSellPrice={oldSellPrice}
                                setOldSellPrice={setOldSellPrice}
                                newSellPrice={newSellPrice}
                                setNewSellPrice={setNewSellPrice}
                                italianDescription={italianDescription}
                                setItalianDescription={setItalianDescription}
                                englishDescription={englishDescription}
                                setEnglishDescription={setEnglishDescription}
                                germanDescription={germanDescription}
                                setGermanDescription={setGermanDescription}
                                handleUpdateSellableInfos={handleUpdateSellableInfos}
                                loadingUpdatingSellableInfos={loadingUpdatingSellableInfos}
                                premiumPalette={premiumPalette}
                            />
                            <Section backgroundColor={primaryColorSecondAlternative}>
                                <FlexColumn>
                                    <MyNormalText text="Duplica" bold fontSize={18}/>
                                    <MyNormalText text="Crea un nuovo articolo identico a questo"/>
                                </FlexColumn>
                                <MainButton
                                    backgroundColor={premiumPalette.secondaryColor}
                                    loading={loadingDuplicateItem}
                                    disabled={loadingDuplicateItem}
                                    text="Duplica"
                                    action={handleDuplicateItem}
                                    aria-label="Duplica articolo"
                                />
                            </Section>
                            <Section backgroundColor={primaryColorSecondAlternative}>
                                <MyNormalText text="Prenotazioni" bold fontSize={18}/>
                                <StyledCalendar
                                    selectable
                                    localizer={localizer}
                                    events={calendarEvents}
                                    views={["month"]}
                                    onSelectSlot={handleSelectRange}
                                    eventPropGetter={eventPropGetter}
                                />
                                <ButtonGroup>
                                    <MainButton
                                        backgroundColor={selectedRange ? premiumPalette.secondaryColor : DISABLED}
                                        disabled={selectedRange === null}
                                        loading={loadingUpdatingItem}
                                        text="Conferma Blocco"
                                        action={handleSaveNewRentedDays}
                                        aria-label="Conferma blocco date"
                                    />
                                    <MainButton
                                        backgroundColor={dateBlockedToRemove ? "red" : DISABLED}
                                        disabled={dateBlockedToRemove === null}
                                        loading={loadingDeletingItemDate}
                                        text="Rimuovi Bloccato"
                                        action={handleRemoveRentedDays}
                                        aria-label="Rimuovi date bloccate"
                                    />
                                </ButtonGroup>
                            </Section>
                        </>
                    )}
                </Container>
            )}
        </ContainerFullPage>
    );
}

// Stili con styled-components
const ContainerFullPage = styled.div`
    z-index: 10;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${GLOBAL_BACKGROUND_POPUP_ABSOLUTE};
    overflow-y: auto;
    padding: 20px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 40px;
    max-width: 800px;
    margin: 5% auto;
    padding-bottom: 20px;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #333;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 20px;
    border-radius: ${GLOBAL_BORDER_RADIUS}px;
    background-color: ${({backgroundColor}) => backgroundColor};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SubSection = styled.div`
    margin-top: 10px;
`;

const ImagesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const ImageWrapper = styled.div`
    flex: 0 0 auto;
`;

const ItemInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StatusContainer = styled.div`
    border-left: 2px solid ${({borderColor}) => borderColor};
    border-bottom: 2px solid ${({borderColor}) => borderColor};
    padding: 5px 10px;
`;

const SellableStatus = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 10px;
    border-left: 1px solid black;
`;

const PriceContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
`;

const DescriptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const InputGroup = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const TextArea = styled.textarea`
    ${globalElementInputStyle.customInput};
    resize: none;
    height: 100px;
    width: 100%;
    border-radius: 4px;
`;

const StyledCalendar = styled(Calendar)`
    height: 700px;
    background: white;
    border-radius: 4px;
    padding: 10px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 10px;
`;

export default ItemDetail;
